<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '800px', maxHeight: '90vh' }"
    :header="modalTitle"
    :dismissable-mask="true"
    class="api-reference-dialog"
  >
    <div class="api-ref">
      <!-- Info banner -->
      <div class="api-ref__info">
        <div class="api-ref__info-grid">
          <span class="api-ref__info-label">
            <i class="fas fa-link" /> {{ t('myShopData.apiReference.baseUrl') }}
          </span>
          <code class="api-ref__info-val">{{ baseUrl }}</code>
          <span class="api-ref__info-label">
            <i class="fas fa-key" /> {{ t('myShopData.apiReference.authHeader') }}
          </span>
          <code class="api-ref__info-val">spm-api-key: &lt;YOUR_KEY&gt;</code>
        </div>
        <div class="api-ref__key-hint">
          <i class="fas fa-info-circle" />
          {{ t('myShopData.apiReference.noKeyYet') }}
          <router-link
            :to="{ name: 'shop.ApiAccess' }"
            class="api-ref__key-link"
            @click="close"
          >
            {{ t('myShopData.apiReference.createKeyLink') }}
          </router-link>
        </div>
      </div>

      <!-- Endpoints -->
      <div class="api-ref__list">
        <div
          v-for="endpoint in endpoints"
          :key="endpoint.key"
          class="ep"
          :class="{ 'ep--open': expandedKey === endpoint.key }"
        >
          <!-- Clickable row -->
          <button
            class="ep__row"
            :class="`ep__row--${endpoint.method.toLowerCase()}`"
            @click="toggle(endpoint.key)"
          >
            <span :class="['ep__badge', `ep__badge--${endpoint.method.toLowerCase()}`]">
              {{ endpoint.method }}
            </span>
            <code class="ep__route">{{ endpoint.route }}</code>
            <span class="ep__summary">{{ endpoint.title }}</span>
            <i :class="expandedKey === endpoint.key ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ep__chevron" />
          </button>

          <!-- Expanded panel -->
          <div
            v-if="expandedKey === endpoint.key"
            class="ep__panel"
          >
            <p class="ep__desc">
              {{ endpoint.description }}
            </p>
            <p
              v-if="endpoint.queryParams"
              class="ep__params"
            >
              <strong>{{ t('myShopData.apiReference.queryParams') }}:</strong>
              <code>limit</code> <code>offset</code> <code>sort</code> <code>order</code> <code>filter</code>
            </p>

            <div class="ep__code-block">
              <div class="ep__code-bar">
                <span class="ep__code-label">curl</span>
                <button
                  class="ep__copy"
                  :class="{ 'ep__copy--ok': copiedKey === endpoint.key }"
                  @click.stop="copyToClipboard(endpoint.curl, endpoint.key)"
                >
                  <i :class="copiedKey === endpoint.key ? 'fas fa-check' : 'far fa-copy'" />
                  {{ copiedKey === endpoint.key ? t('myShopData.apiReference.copied') : '' }}
                </button>
              </div>
              <pre class="ep__code"><template v-for="(token, idx) in endpoint.tokens" :key="idx"><span :class="token.cls">{{ token.text }}</span></template></pre>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <SpmButton
        :label="t('myShopData.apiReference.close')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { fetchCustomSchemaDefinition } from '@/composables/custom-data/customData';
import type { ActionPayload, ApiReferencePayload } from './types';

interface FieldDef {
  name: string;
  type: string;
}

interface Token {
  text: string;
  cls: string;
}

function getExampleValue(type: string): string {
  switch (type) {
    case 'number':
    case 'integer':
    case 'decimal':
    case 'float':
      return '123';
    case 'bool':
    case 'boolean':
      return 'true';
    case 'date':
      return '"2024-01-15"';
    case 'datetime':
      return '"2024-01-15T10:30:00Z"';
    case 'json':
      return '{}';
    case 'geolocation':
      return '"48.8566,2.3522"';
    default:
      return '"example"';
  }
}

function tokenize(curl: string): Token[] {
  const tokens: Token[] = [];
  const raw = curl;
  let i = 0;

  while (i < raw.length) {
    if (raw.substring(i).startsWith('curl')) {
      tokens.push({ text: 'curl', cls: 'tk-cmd' });
      i += 4;
      continue;
    }

    if (raw[i] === '-' && i > 0 && raw[i - 1] === ' ' && /[XHd]/.test(raw[i + 1] || '')) {
      const flag = raw.substring(i, i + 2);
      tokens.push({ text: flag, cls: 'tk-flag' });
      i += 2;
      continue;
    }

    const methodMatch = raw.substring(i).match(/^(GET|POST|PATCH|PUT|DELETE)\b/);
    if (methodMatch) {
      tokens.push({ text: methodMatch[1], cls: 'tk-method' });
      i += methodMatch[1].length;
      continue;
    }

    if (raw[i] === "'") {
      let end = raw.indexOf("'", i + 1);
      if (end === -1) end = raw.length - 1;
      const content = raw.substring(i, end + 1);
      if (content.includes('http')) {
        tokens.push({ text: content, cls: 'tk-url' });
      } else if (content.includes('{') || content.includes('[')) {
        tokens.push({ text: content, cls: 'tk-json' });
      } else {
        tokens.push({ text: content, cls: 'tk-string' });
      }
      i = end + 1;
      continue;
    }

    if (raw[i] === '\\' && raw[i + 1] === '\n') {
      tokens.push({ text: ' \\\n', cls: 'tk-plain' });
      i += 2;
      continue;
    }

    tokens.push({ text: raw[i], cls: 'tk-plain' });
    i += 1;
  }

  return tokens;
}

export default defineComponent({
  name: 'ApiReferenceModal',

  components: {
    Dialog,
    SpmButton,
  },

  setup(_, { expose }) {
    const { t } = useI18n();

    const isVisible = ref(false);
    const schemaName = ref('');
    const definitionId = ref<number>(0);
    const fields = ref<FieldDef[]>([]);
    const copiedKey = ref<string | null>(null);
    const expandedKey = ref<string | null>(null);

    const baseUrl = computed(() => {
      const clientApiUrl = process.env.VUE_APP_NESTJS_CLIENT_API_URL || '';
      const version = process.env.VUE_APP_NESTJS_CLIENT_API_VERSION || 'v1';
      return `${clientApiUrl}/${version}`;
    });

    const modalTitle = computed(() => `${schemaName.value} — ${t('myShopData.apiReference.title')}`);

    const toggle = (key: string) => {
      expandedKey.value = expandedKey.value === key ? null : key;
    };

    const buildJsonBody = (fieldList: FieldDef[], indent: number) => {
      const pad = ' '.repeat(indent);
      const innerPad = ' '.repeat(indent + 2);
      if (fieldList.length === 0) {
        return `${pad}{\n${innerPad}"field1": "value1"\n${pad}}`;
      }
      const lines = fieldList.map((f) => `${innerPad}"${f.name}": ${getExampleValue(f.type)}`);
      return `${pad}{\n${lines.join(',\n')}\n${pad}}`;
    };

    const endpoints = computed(() => {
      const id = definitionId.value || '{DEFINITION_ID}';
      const url = baseUrl.value;

      const postBody = buildJsonBody(fields.value, 4);
      const patchFields = fields.value.length > 0 ? fields.value.slice(0, 2) : [{ name: 'field1', type: 'text' }];
      const patchBody = buildJsonBody(patchFields, 2);

      const curlPost = `curl -X POST '${url}/custom-data-records/${id}' \\
  -H 'Content-Type: application/json' \\
  -H 'spm-api-key: YOUR_API_KEY' \\
  -d '{
  "records": [
${postBody}
  ]
}'`;

      const curlGet = `curl -X GET '${url}/custom-data-records/${id}?limit=10&offset=0' \\
  -H 'spm-api-key: YOUR_API_KEY'`;

      const curlPatch = `curl -X PATCH '${url}/custom-data-records/${id}/1' \\
  -H 'Content-Type: application/json' \\
  -H 'spm-api-key: YOUR_API_KEY' \\
  -d '{
${patchBody.trimStart()}
}'`;

      const curlDelete = `curl -X DELETE '${url}/custom-data-records/${id}?id=1' \\
  -H 'spm-api-key: YOUR_API_KEY'`;

      const curlBulkDelete = `curl -X POST '${url}/custom-data-records/${id}/bulk-delete' \\
  -H 'Content-Type: application/json' \\
  -H 'spm-api-key: YOUR_API_KEY' \\
  -d '{
  "ids": [1, 2, 3]
}'`;

      return [
        {
          key: 'post',
          method: 'POST',
          route: `/custom-data-records/${id}`,
          title: t('myShopData.apiReference.createOrUpdate'),
          description: t('myShopData.apiReference.createOrUpdateDesc'),
          queryParams: false,
          curl: curlPost,
          tokens: tokenize(curlPost),
        },
        {
          key: 'get',
          method: 'GET',
          route: `/custom-data-records/${id}`,
          title: t('myShopData.apiReference.listRecords'),
          description: t('myShopData.apiReference.listRecordsDesc'),
          queryParams: true,
          curl: curlGet,
          tokens: tokenize(curlGet),
        },
        {
          key: 'patch',
          method: 'PATCH',
          route: `/custom-data-records/${id}/{recordId}`,
          title: t('myShopData.apiReference.updateRecord'),
          description: t('myShopData.apiReference.updateRecordDesc'),
          queryParams: false,
          curl: curlPatch,
          tokens: tokenize(curlPatch),
        },
        {
          key: 'delete',
          method: 'DELETE',
          route: `/custom-data-records/${id}?id={recordId}`,
          title: t('myShopData.apiReference.deleteRecord'),
          description: t('myShopData.apiReference.deleteRecordDesc'),
          queryParams: false,
          curl: curlDelete,
          tokens: tokenize(curlDelete),
        },
        {
          key: 'bulkDelete',
          method: 'POST',
          route: `/custom-data-records/${id}/bulk-delete`,
          title: t('myShopData.apiReference.bulkDelete'),
          description: t('myShopData.apiReference.bulkDeleteDesc'),
          queryParams: false,
          curl: curlBulkDelete,
          tokens: tokenize(curlBulkDelete),
        },
      ];
    });

    const copyToClipboard = async (text: string, key: string) => {
      try {
        await navigator.clipboard.writeText(text);
        copiedKey.value = key;
        setTimeout(() => {
          copiedKey.value = null;
        }, 2000);
      } catch {
        // Fallback silently
      }
    };

    const open = async (payload: ActionPayload | ApiReferencePayload) => {
      expandedKey.value = null;
      isVisible.value = true;

      // Mode "inline" : fields passés directement depuis l'éditeur (création ou édition)
      if ('schemaName' in payload) {
        schemaName.value = payload.schemaName;
        definitionId.value = payload.definitionId ?? 0;
        fields.value = (payload.fields || []).map((f: any) => ({
          name: f.name || 'unknown',
          type: f.type || 'text',
        }));
        return;
      }

      // Mode classique : depuis la liste des schemas (ActionPayload)
      schemaName.value = payload.schemaItem.schema_name;
      definitionId.value = payload.schemaItem.id_custom_object ?? 0;
      fields.value = [];

      if (definitionId.value) {
        const result = await fetchCustomSchemaDefinition(definitionId.value);
        if (result.success && result.data) {
          const def = result.data;
          if (Array.isArray(def.fields)) {
            fields.value = def.fields.map((f: any) => ({
              name: f.name || 'unknown',
              type: f.type || 'text',
            }));
          }
        }
      }
    };

    const close = () => {
      isVisible.value = false;
    };

    expose({ open });

    return {
      t,
      isVisible,
      definitionId,
      baseUrl,
      modalTitle,
      copiedKey,
      expandedKey,
      endpoints,
      toggle,
      copyToClipboard,
      close,
    };
  },
});
</script>

<style lang="scss" scoped>
$mono: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
$code-bg: #1e1e2e;

.api-ref {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;

  /* ---- Info banner ---- */
  &__info {
    background: var(--surface-ground);
    border: 1px solid var(--surface-border);
    border-radius: 8px;
    padding: 0.75rem 1rem;
  }

  &__info-grid {
    display: grid;
    grid-template-columns: auto auto;
    justify-content: start;
    gap: 0.35rem 0.75rem;
    align-items: center;
  }

  &__info-label {
    font-size: 0.8rem;
    color: var(--text-color-secondary);
    white-space: nowrap;

    i {
      width: 14px;
      text-align: center;
      margin-right: 0.25rem;
      font-size: 0.75rem;
    }
  }

  &__info-val {
    font-family: $mono;
    font-size: 0.8rem;
    color: var(--primary-color);
    background: var(--surface-hover);
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    white-space: nowrap;
  }

  &__key-hint {
    font-size: 0.775rem;
    color: var(--text-color-secondary);
    margin-top: 0.6rem;
    padding-top: 0.6rem;
    border-top: 1px solid var(--surface-border);
    display: flex;
    align-items: center;
    gap: 0.35rem;

    i { font-size: 0.7rem; }
  }

  &__key-link {
    color: var(--primary-color);
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  /* ---- Endpoint list ---- */
  &__list {
    display: flex;
    flex-direction: column;
    gap: 0;
    border: 1px solid var(--surface-border);
    border-radius: 8px;
    overflow: hidden;
  }
}

/* ---- Single endpoint ---- */
.ep {
  &:not(:last-child) {
    border-bottom: 1px solid var(--surface-border);
  }

  /* Clickable row */
  &__row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    width: 100%;
    padding: 0.55rem 0.85rem;
    border: none;
    background: var(--surface-card);
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    font-size: inherit;
    transition: background 0.15s;

    &:hover {
      background: var(--surface-hover);
    }

    &--post { border-left: 3px solid #49cc90; }
    &--get { border-left: 3px solid #61affe; }
    &--patch { border-left: 3px solid #fca130; }
    &--delete { border-left: 3px solid #f93e3e; }
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.15rem 0;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 700;
    font-family: $mono;
    color: #fff;
    min-width: 52px;
    letter-spacing: 0.03em;
    flex-shrink: 0;

    &--post { background: #49cc90; }
    &--get { background: #61affe; }
    &--patch { background: #fca130; }
    &--delete { background: #f93e3e; }
  }

  &__route {
    font-family: $mono;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-color);
    flex-shrink: 0;
  }

  &__summary {
    flex: 1;
    font-size: 0.8rem;
    color: var(--text-color-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chevron {
    font-size: 0.65rem;
    color: var(--text-color-secondary);
    flex-shrink: 0;
    transition: transform 0.2s;
  }

  /* Expanded panel */
  &__panel {
    padding: 0.75rem 0.85rem 0.85rem;
    padding-left: calc(0.85rem + 3px); /* align with row content */
    background: var(--surface-ground);
    border-top: 1px solid var(--surface-border);
    text-align: left;
  }

  &__desc {
    margin: 0 0 0.5rem;
    font-size: 0.825rem;
    color: var(--text-color-secondary);
    line-height: 1.4;
    text-align: left;
  }

  &__params {
    margin: 0 0 0.6rem;
    font-size: 0.775rem;
    color: var(--text-color-secondary);

    strong {
      color: var(--text-color);
    }

    code {
      font-family: $mono;
      font-size: 0.725rem;
      background: var(--surface-card);
      padding: 0.1rem 0.35rem;
      border-radius: 3px;
      color: var(--text-color);
      margin: 0 0.1rem;
    }
  }

  /* Code block */
  &__code-block {
    background: $code-bg;
    border-radius: 6px;
    overflow: hidden;
  }

  &__code-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.3rem 0.7rem;
    background: #27273a;
  }

  &__code-label {
    font-size: 0.7rem;
    font-family: $mono;
    color: #7f849c;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__copy {
    background: transparent;
    border: 1px solid #44445a;
    border-radius: 4px;
    padding: 0.15rem 0.4rem;
    cursor: pointer;
    font-size: 0.7rem;
    color: #7f849c;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-family: inherit;
    transition: all 0.15s;

    &:hover {
      background: #33334a;
      color: #bac2de;
      border-color: #55556a;
    }

    &--ok {
      color: #49cc90;
      border-color: #49cc90;
    }
  }

  &__code {
    margin: 0;
    padding: 0.65rem 0.75rem;
    font-size: 0.75rem;
    font-family: $mono;
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.5;
    color: #cdd6f4;
    text-align: left;
  }

  /* Open state highlight */
  &--open > .ep__row {
    background: var(--surface-ground);
  }
}

/* Syntax highlighting */
.tk-cmd { color: #89b4fa; font-weight: 600; }
.tk-flag { color: #94e2d5; }
.tk-method { color: #f9e2af; font-weight: 700; }
.tk-url { color: #a6e3a1; }
.tk-string { color: #a6e3a1; }
.tk-json { color: #cba6f7; }
.tk-plain { color: #cdd6f4; }
</style>
